import { TokenKind } from '../../json/tokens';
import { isCloser, structuralTokens } from '../support';
import type { RepairContext, RepairProposal, RepairRule } from '../types';

/**
 * Delete a `,` whose next significant token closes the container: `{"a":1,}`, `[1,2,]`,
 * `{"a":1,\n}`.
 *
 * Written in the vocabulary ADR-0011 insists on — "a `Comma` token whose next significant
 * token is a closer" — rather than the legacy `/,\s*([}\]])/g`, which also matched inside
 * string literals and turned `{"a":"x,}y","b":1,}` into `{"a":"x}y","b":1}`: a document that
 * parses, with a silently altered value. That is the worst outcome a data tool can produce,
 * and it is the negative case pinned in this rule's test.
 */
export const removeTrailingCommaRule: RepairRule = {
  id: 'remove-trailing-comma',
  confidence: 'safe',
  priority: 20,
  resolves: ['syntax/trailing-comma'],

  detect(ctx: RepairContext): readonly RepairProposal[] {
    const sig = structuralTokens(ctx.source, ctx.tokens);
    const proposals: RepairProposal[] = [];

    for (let i = 0; i < sig.length; i += 1) {
      const token = sig[i];
      const next = sig[i + 1];
      if (token === undefined || next === undefined) continue;
      if (token.kind !== TokenKind.Comma) continue;
      if (!isCloser(next)) continue;

      proposals.push({
        ruleId: 'remove-trailing-comma',
        span: { from: token.from, to: token.to },
        edits: [{ from: token.from, to: token.to, insert: '' }],
        labelCode: 'fix.remove-trailing-comma',
        resolves: 'syntax/trailing-comma',
      });
    }

    return proposals;
  },
};
